import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LuPlane, LuHouse, LuSettings2, LuCpu, LuShieldCheck,
  LuLayoutGrid, LuMail, LuMenu, LuX, LuNewspaper,
  LuFileText, LuChevronDown, LuDownload,
} from 'react-icons/lu';
import logo from '../../loggo.png';

const PRIMARY_LINKS = [
  { label: 'Home',         to: '/',            icon: <LuHouse size={15} /> },
  { label: 'How It Works', to: '/how-it-works', icon: <LuSettings2 size={15} /> },
  { label: 'Technology',   to: '/technology',   icon: <LuCpu size={15} /> },
  { label: 'Safety',       to: '/safety',       icon: <LuShieldCheck size={15} /> },
  { label: 'Types',        to: '/types',        icon: <LuLayoutGrid size={15} /> },
];

const RESOURCE_LINKS = [
  { label: 'Newsletter',     to: '/newsletter', icon: <LuNewspaper size={15} />, desc: 'Monthly airport tech insights' },
  { label: 'Free Brochure',  to: '/brochure',   icon: <LuFileText size={15} />,  desc: '60-page PDF download' },
  { label: 'Contact Us',     to: '/contact',    icon: <LuMail size={15} />,      desc: 'Get in touch with us' },
];

function Navbar() {
  const [scrolled, setScrolled]     = useState(false);
  const [open, setOpen]             = useState(false);
  const [dropOpen, setDropOpen]     = useState(false);
  const dropRef                     = useRef(null);
  const location                    = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setDropOpen(false); window.scrollTo(0, 0); }, [location]);

  /* close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      <nav className={`navbar${scrolled ? ' scrolled' : ''}`} role="navigation" aria-label="Main navigation">
        {/* Logo */}
        <NavLink to="/" className="nav-logo" aria-label="AeroConveyor — Home">
            <img src={logo} alt="AeroConveyor Logo" width="130px" />
        </NavLink>

        {/* Desktop links */}
        <div className="nav-links">
          {PRIMARY_LINKS.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) => isActive ? 'active' : ''}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {l.icon}
              {l.label}
            </NavLink>
          ))}

          {/* Resources dropdown */}
          <div className="nav-dropdown-wrap" ref={dropRef}>
            <button
              className={`nav-dropdown-trigger${dropOpen ? ' open' : ''}`}
              onClick={() => setDropOpen(o => !o)}
              aria-expanded={dropOpen}
              aria-haspopup="true"
            >
              Resources
              <LuChevronDown size={13} className="nav-drop-chevron" />
            </button>
            {dropOpen && (
              <div className="nav-dropdown-panel" role="menu">
                {RESOURCE_LINKS.map(r => (
                  <NavLink
                    key={r.to}
                    to={r.to}
                    className={({ isActive }) => `nav-drop-item${isActive ? ' active' : ''}`}
                    role="menuitem"
                  >
                    <div className="nav-drop-icon">{r.icon}</div>
                    <div>
                      <div className="nav-drop-label">{r.label}</div>
                      <div className="nav-drop-desc">{r.desc}</div>
                    </div>
                  </NavLink>
                ))}
                <div className="nav-drop-footer">
                  <NavLink to="/brochure" className="btn btn-sm btn-primary" style={{ justifyContent: 'center', width: '100%' }}>
                    <LuDownload size={13} /> Download Free PDF
                  </NavLink>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="nav-cta-area">
          <NavLink to="/contact" className="nav-cta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <LuMail size={15} /> Contact
          </NavLink>
        </div>

        {/* Hamburger */}
        <button
          className={`nav-hamburger${open ? ' open' : ''}`}
          onClick={() => setOpen(o => !o)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="mobile-nav"
        >
          {open ? <LuX size={22} /> : <LuMenu size={22} />}
        </button>
      </nav>

      {/* Mobile nav */}
      <div
        id="mobile-nav"
        className={`mobile-nav${open ? ' open' : ''}`}
        role="navigation"
        aria-label="Mobile navigation"
      >
        {PRIMARY_LINKS.map(l => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === '/'}
            className={({ isActive }) => isActive ? 'active' : ''}
          >
            {l.icon}
            {l.label}
          </NavLink>
        ))}

        <div className="mobile-nav-divider" />
        <div className="mobile-nav-section-label">Resources</div>

        {RESOURCE_LINKS.map(r => (
          <NavLink
            key={r.to}
            to={r.to}
            className={({ isActive }) => isActive ? 'active' : ''}
          >
            {r.icon}
            {r.label}
          </NavLink>
        ))}

        <div className="mobile-nav-divider" />

        <div className="mobile-nav-cta">
          <NavLink to="/brochure" className="btn btn-primary btn-sm">
            <LuDownload size={14} /> Download Free Brochure
          </NavLink>
        </div>

        <div className="mobile-nav-footer">
          <LuPlane size={14} />
          Airport Baggage Technology Resource
        </div>
      </div>

      {/* overlay to close dropdown */}
      {dropOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 998 }}
          onClick={() => setDropOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}

export default Navbar;
